import React, { useState } from "react";

import { Divider, Typography } from "@mui/material";

import {
  LicenseCountButton,
  LicenseCountInput,
} from "@app/analytics/account/Billing/components/styles";
import {
  getPlanPriceByPriceId,
  isZoomPlan,
} from "@app/analytics/account/Billing/utils";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import useBillingSubscription from "@app/analytics/hooks/billing/useBillingSubscription";
import * as Events from "@app/analytics/tracking/events";
import { showToast } from "@app/common/hooks/useToasts";
import ROUTES from "@app/common/routes";
import { pluralizePhrase } from "@app/common/utils";
import type { EnterpriseWorkspace } from "@app/common/workspaces/types";
import Config from "@app/config";
import { formatCurrency, parseUTCDateTimeFromString } from "@app/utils/utils";

import Alert from "@rui/Alert";
import ConfirmationModal, {
  ConfirmationModalProps,
} from "@rui/ConfirmationModal";
import { Link } from "@rui/Link";
import Stack, { Row } from "@rui/Stack/Stack";
import { Bold } from "@rui/styles";

interface RemoveLicensesModalProps
  extends Pick<ConfirmationModalProps, "open" | "onClose"> {
  workspaceId?: EnterpriseWorkspace["id"];
}

const MIN_LICENSES_TO_REMOVE = 0;

const RemoveLicensesModal = ({
  onClose,
  open,
  workspaceId,
}: RemoveLicensesModalProps) => {
  const { currentPlan, isTrial, licenses, subscription } = useBillingContext();
  const { updateSubscriptionQuantity } = useBillingSubscription();
  const [numToRemove, setNumToRemove] = useState(MIN_LICENSES_TO_REMOVE);

  const willPayForLicenses = !isTrial && !isZoomPlan(subscription.plan);
  const numLicensesAfterRemoval = Math.max(licenses.numActive - numToRemove, 1);

  const monthlyPricePerLicense =
    getPlanPriceByPriceId(
      subscription.scheduledUpdate?.stripePriceId ?? subscription.stripePriceId
    ) ?? 0;
  const nextBillingPeriodCadence =
    subscription.scheduledUpdate?.billingCadence ?? subscription.billingCadence;
  const pricePerLicense =
    monthlyPricePerLicense * (nextBillingPeriodCadence === "annual" ? 12 : 1);

  const minimumAllowableLicenses =
    Config.billing.licenseMinimum[
      subscription.scheduledUpdate?.plan ?? subscription.plan
    ] ?? 1;

  const canRemoveWorkspaceMembersToRemoveMoreLicenses =
    licenses.numClaimed > minimumAllowableLicenses;

  const maxRemovableLicenses = Math.max(
    0,
    Math.min(
      licenses.numUnclaimed,
      subscription.quantity - minimumAllowableLicenses
    )
  );

  const handleUpdateLicenseCount = (
    update: { delta: -1 | 1 } | { count: number }
  ) => {
    setNumToRemove((prevNum) =>
      "count" in update
        ? Math.min(update.count, maxRemovableLicenses)
        : prevNum + update.delta
    );
  };

  const handleIncrementDecrementLicenseCount = (delta: -1 | 1) => () => {
    handleUpdateLicenseCount({ delta });
  };

  const handleChangeLicenseCount: React.ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    handleUpdateLicenseCount({ count: parseInt(event.target.value) });
  };

  const handleClose = () => {
    onClose();
    setNumToRemove(MIN_LICENSES_TO_REMOVE);
  };

  const handleConfirm = () => {
    updateSubscriptionQuantity.mutate(-numToRemove, {
      onSuccess: () => {
        Events.removePlanLicenses({ seats: numToRemove });
        showToast({
          severity: "success",
          message: "Licenses updated!",
        });
      },
      onError: () => {
        showToast({
          severity: "error",
          message:
            "Oops! Something went wrong and your update could not be processed. Please refresh the page or try again later.",
        });
      },
      onSettled: handleClose,
    });
  };

  const confirmDisabled = numToRemove > maxRemovableLicenses || numToRemove < 1;
  const formattedPeriodEnd = subscription.currentPeriodEnd
    ? parseUTCDateTimeFromString(subscription.currentPeriodEnd).toLocaleString({
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : undefined;

  const showLicenseMinimumDisclaimer =
    minimumAllowableLicenses > 1 &&
    !canRemoveWorkspaceMembersToRemoveMoreLicenses;

  return (
    <ConfirmationModal
      loading={updateSubscriptionQuantity.isPending}
      open={open}
      onClose={handleClose}
      size="xl"
      title="Remove Licenses"
      onConfirm={handleConfirm}
      confirmProps={{
        label: "Remove licenses",
        type: "negative",
        disabled: confirmDisabled,
      }}
    >
      <Stack spacing={2}>
        <Typography variant="bodyPrimaryReg">
          You currently have{" "}
          <Bold>{pluralizePhrase(maxRemovableLicenses, "unused license")}</Bold>
          {workspaceId ? " in your Workspace " : " "}
          that can be removed.
          {canRemoveWorkspaceMembersToRemoveMoreLicenses && workspaceId ? (
            <>
              {" "}
              If you would like to remove more,{" "}
              <Link to={ROUTES.workspaceSettingsPeopleById(workspaceId)}>
                remove members
              </Link>{" "}
              from your Workspace first.
            </>
          ) : showLicenseMinimumDisclaimer ? (
            <>
              {" "}
              The {currentPlan.label} plan requires a minimum of{" "}
              {minimumAllowableLicenses} licenses.
            </>
          ) : null}
        </Typography>

        {willPayForLicenses && (
          <Alert severity="warning" embedded alignItems="flex-start">
            Removing licenses will only be reflected in the next billing period,
            and will not result in proration for the current billing period.
          </Alert>
        )}
        <Divider />
        <Row variant="apart" sx={{ px: 1.5 }}>
          <Typography variant="bodyPrimaryReg">
            Number of licenses to remove
          </Typography>
          <Row>
            <LicenseCountButton
              kind="decrement"
              onClick={handleIncrementDecrementLicenseCount(-1)}
              disabled={numToRemove <= MIN_LICENSES_TO_REMOVE}
            />
            <LicenseCountInput
              onChange={handleChangeLicenseCount}
              value={numToRemove}
              max={maxRemovableLicenses}
              min={MIN_LICENSES_TO_REMOVE}
            />
            <LicenseCountButton
              kind="increment"
              onClick={handleIncrementDecrementLicenseCount(1)}
              disabled={numToRemove >= maxRemovableLicenses}
            />
          </Row>
        </Row>
        {willPayForLicenses && (
          <>
            <Divider />
            <Row
              justifyContent="space-between"
              sx={{ color: "shades.neutral.500", px: 1.5 }}
            >
              <Typography variant="bodyPrimaryMed">Current cost</Typography>
              <Typography variant="bodyPrimaryMed">
                {formatCurrency(pricePerLicense * subscription.quantity)}
              </Typography>
            </Row>
            <Row
              justifyContent="space-between"
              sx={{ color: "shades.neutral.500", px: 1.5 }}
            >
              <Typography variant="bodyPrimaryMed">
                Licenses removed{numToRemove > 0 ? ` (${numToRemove})` : ""}
              </Typography>
              <Typography variant="bodyPrimaryMed">
                {numToRemove > 0
                  ? `(${formatCurrency(pricePerLicense * numToRemove)})`
                  : "-"}
              </Typography>
            </Row>
            <Divider />
            <Row
              justifyContent="space-between"
              sx={{ color: "shades.neutral.700", px: 1.5 }}
            >
              <Stack spacing={1}>
                {formattedPeriodEnd && (
                  <Typography variant="bodyPrimaryMed">
                    Total due on {formattedPeriodEnd} (next billing period)
                  </Typography>
                )}
                <Typography variant="bodySecondaryReg">
                  {formatCurrency(pricePerLicense, { digits: 0 })} x{" "}
                  {pluralizePhrase(numLicensesAfterRemoval, "license")} x 1{" "}
                  {subscription.billingCadence === "monthly" ? "month" : "year"}
                </Typography>
              </Stack>
              <Typography variant="bodyPrimaryMed">
                {formatCurrency(pricePerLicense * numLicensesAfterRemoval)}
              </Typography>
            </Row>
          </>
        )}
        <Divider />
      </Stack>
    </ConfirmationModal>
  );
};

export default RemoveLicensesModal;
